import { spawn } from "node:child_process";
import { LocalCliError } from "./errors.js";
import { signAppJwt } from "@m8t-stack/github-app-auth";

export async function createBlankRepo(args: {
  token: string;
  org: string;
  name: string;
  private?: boolean;
}): Promise<{ cloneUrl: string; defaultBranch: string }> {
  const res = await fetch(`https://api.github.com/orgs/${args.org}/repos`, {
    method: "POST",
    headers: {
      Authorization: `token ${args.token}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
      "User-Agent": "m8t",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name: args.name, private: args.private !== false, auto_init: false }),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_REPO_CREATE_FAILED",
      message: `POST /orgs/${args.org}/repos HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  const j = JSON.parse(text) as { clone_url: string; default_branch?: string };
  return { cloneUrl: j.clone_url, defaultBranch: j.default_branch ?? "main" };
}

function run(cmd: string, cmdArgs: string[], cwd: string, env?: NodeJS.ProcessEnv): Promise<void> {
  return new Promise((resolve, reject) => {
    const c = spawn(cmd, cmdArgs, { cwd, env: { ...process.env, ...env }, stdio: ["ignore", "pipe", "pipe"] });
    let err = "";
    c.stderr.on("data", (d: Buffer) => {
      err += d.toString();
    });
    c.on("close", (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(
          new LocalCliError({
            code: "GH_APP_PUSH_FAILED",
            message: `${cmd} ${cmdArgs.join(" ")} failed: ${err.slice(0, 300)}`,
          }),
        );
      }
    });
  });
}

/**
 * Push the contents of `sourceDir` (a rendered brain-template+seed) to a freshly
 * created blank repo using an installation token (embedded via x-access-token).
 *
 * Pass `remoteOverride` to substitute the computed https remote — useful in tests
 * so no network or real token is needed (e.g. a `file://` path to a local bare repo).
 */
export async function pushSeed(args: {
  token: string;
  org: string;
  name: string;
  branch: string;
  sourceDir: string;
  remoteOverride?: string;
}): Promise<void> {
  const remote =
    args.remoteOverride ?? `https://x-access-token:${args.token}@github.com/${args.org}/${args.name}.git`;
  const env: NodeJS.ProcessEnv = {
    GIT_AUTHOR_NAME: "m8t",
    GIT_AUTHOR_EMAIL: "m8t@users.noreply.github.com",
    GIT_COMMITTER_NAME: "m8t",
    GIT_COMMITTER_EMAIL: "m8t@users.noreply.github.com",
  };
  await run("git", ["init", "-q", "-b", args.branch], args.sourceDir, env);
  await run("git", ["add", "-A"], args.sourceDir, env);
  await run("git", ["commit", "-q", "-m", "seed: initial brain"], args.sourceDir, env);
  await run("git", ["remote", "add", "origin", remote], args.sourceDir, env);
  await run("git", ["push", "-q", "origin", args.branch], args.sourceDir, env);
}

const GH_API = "https://api.github.com";
const GH_REQUEST_TIMEOUT_MS = 30_000;
function ghHeaders(token: string, scheme: "token" | "Bearer" = "token"): Record<string, string> {
  return {
    Authorization: `${scheme} ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
    "User-Agent": "m8t",
    "Content-Type": "application/json",
  };
}

/** Mint a GitHub App installation token from a local PEM (the no-KV, local-bootstrap path). */
export async function mintInstallationTokenFromPem(args: {
  appId: string; privateKeyPem: string; installationId: string; fetchImpl?: typeof fetch;
}): Promise<string> {
  const doFetch = args.fetchImpl ?? fetch;
  const jwt = signAppJwt({ appId: args.appId, privateKeyPem: args.privateKeyPem });
  const res = await doFetch(`${GH_API}/app/installations/${args.installationId}/access_tokens`, {
    method: "POST", headers: ghHeaders(jwt, "Bearer"), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_TOKEN_MINT_FAILED",
      message: `POST /app/installations/${args.installationId}/access_tokens HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  return (JSON.parse(text) as { token: string }).token;
}

/** Read a file's decoded text from a repo via the Contents API, or null if it doesn't exist. */
export async function readRepoFileViaApp(args: {
  token: string; repo: string; path: string; fetchImpl?: typeof fetch;
}): Promise<string | null> {
  const doFetch = args.fetchImpl ?? fetch;
  const res = await doFetch(`${GH_API}/repos/${args.repo}/contents/${args.path}`, { headers: ghHeaders(args.token), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
  if (res.status === 404) return null;
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_FILE_READ_FAILED",
      message: `GET /repos/${args.repo}/contents/${args.path} HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  const data = JSON.parse(text) as { content?: string };
  return Buffer.from(data.content ?? "", "base64").toString("utf8");
}

/** Commit multiple files in ONE commit to an existing repo on `branch` via the Git Data API. */
export async function commitFilesViaApp(args: {
  token: string; repo: string; branch: string;
  files: { path: string; content: string }[]; message: string; fetchImpl?: typeof fetch;
}): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  const api = async <T>(p: string, init?: { method: string; body: string }): Promise<T> => {
    const res = await doFetch(`${GH_API}/repos/${args.repo}${p}`, init ? { ...init, headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) } : { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
    const text = await res.text();
    if (!res.ok) {
      throw new LocalCliError({
        code: "GH_APP_COMMIT_FAILED",
        message: `${init?.method ?? "GET"} ${p} HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
      });
    }
    return (text ? JSON.parse(text) : {}) as T;
  };
  const ref = await api<{ object: { sha: string } }>(`/git/ref/heads/${args.branch}`);
  const baseCommitSha = ref.object.sha;
  const baseCommit = await api<{ tree: { sha: string } }>(`/git/commits/${baseCommitSha}`);
  const blobs = await Promise.all(args.files.map((f) =>
    api<{ sha: string }>(`/git/blobs`, { method: "POST", body: JSON.stringify({ content: f.content, encoding: "utf-8" }) })));
  const tree = await api<{ sha: string }>(`/git/trees`, {
    method: "POST",
    body: JSON.stringify({
      base_tree: baseCommit.tree.sha,
      tree: args.files.map((f, i) => ({ path: f.path, mode: "100644", type: "blob", sha: blobs[i].sha })),
    }),
  });
  const commit = await api<{ sha: string }>(`/git/commits`, {
    method: "POST", body: JSON.stringify({ message: args.message, tree: tree.sha, parents: [baseCommitSha] }),
  });
  await api(`/git/refs/heads/${args.branch}`, { method: "PATCH", body: JSON.stringify({ sha: commit.sha }) });
}
